import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm,
  Popover
} from 'antd';
import { faCheckCircle, faClipboardCheck, faEye, faFileExcel, faPencil, faPlus, faTimesCircle, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1, H4 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import {
  toCurrency,
  thousand,
  getDatePresets,
  getSourceAssetDetailsReportColumns,
  cleanSourceAssetDetailsReportDataForExport,
  getSourceAssetDetailsReportExportFields
} from '../../../functions';
import Select from '../../shared-components/form/Select';
import CustomButton from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import DateSelector from '../../shared-components/form/DatePicker';
import Input from '../../shared-components/form/Input';

function SaleOrdersAssetDetail() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [serializedFilter, setSerializedFilter] = useState(0);

  function getData(selectedPage, sTerm) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    setLastSearch(sTerm);
    setCurrentPage(selectedPage);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/orders/date', {
        StartDate: startString,
        EndDate: endString,
        Page: currentPage,
        Size: pageSize,
        ForExport: false,
        Query: sTerm,
        SerializedFilter: serializedFilter
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function exportData() {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/orders/date/excel', {
        StartDate: startString,
        EndDate: endString,
        ForExport: true,
        Query: lastSearch,
        SerializedFilter: serializedFilter
      }, {
        responseType: 'blob'
      })
      .then((response) => {
        setLoading(false);
        const fileName = `${t('dispatch_report')} ${startDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}-${endDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}.xlsx`;
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function isRangeMoreThanMonth() {
    try {
      const startDate = dayjs(dateRange?.start);
      const endDate = dayjs(dateRange?.end);
      const monthDiff = endDate.diff(startDate, 'month', true);
      return monthDiff > 1;
    } catch (err) {
      return false;
    }
  }

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    getData(0, lastSearch);
  }, [pageSize, dateRange, serializedFilter]); // add filters sorts here

  return (
    <MainPage>
      <H1 text={t('dispatch_report')} />
      <div className="space-y-2">
        <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          {/* <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('type')}
            id="typefilter"
            name="typefilter"
            value={incidentalTypeFilter}
            onChange={(obj) => setIncidentalTypeFilter(obj.target.value)}
            options={types}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('approval_status')}
            id="approvalfilter"
            name="approvalfilter"
            value={incidentalApprovalFilter}
            onChange={(obj) => setIncidentalApprovalFilter(obj.target.value)}
            options={statusFilters}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('payment_status')}
            id="paymentfilter"
            name="paymentfilter"
            value={incidentalPaymentFilter}
            onChange={(obj) => setIncidentalPaymentFilter(obj.target.value)}
            options={statusFilters}
          /> */}
          <Select
            className="col-span-1 md:col-span-2 lg:col-start-3 lg:col-span-1 xl:col-start-4 mt-2"
            disabled={loading}
            label={t('serialized_filter')}
            id="serializedfilter"
            name="serializedfilter"
            value={serializedFilter}
            onChange={(obj) => setSerializedFilter(parseInt(obj.target.value, 10))}
            options={[
              {
                key: '0',
                value: '0',
                text: t('both')
              },
              {
                key: '1',
                value: '1',
                text: t('serialized')
              },
              {
                key: '2',
                value: '2',
                text: t('non_serialized')
              },
            ]}
          />
          <div className="col-span-1 md:col-span-2 lg:col-start-4 lg:col-span-1 xl:col-start-5">
            <RangePicker
              disabled={loading}
              className="w-full h-11 mt-2 bg-transparent"
              disabledDate={(current) => current && current.valueOf() > Date.now()}
              presets={getDatePresets(t)}
              value={[
                dayjs(dateRange?.start),
                dayjs(dateRange?.end)
              ]}
              onChange={(obj) => {
                setDateRange({
                  start: obj[0],
                  end: obj[1]
                });
              }}
              format="MMM DD, YYYY"
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <Input
            label={t('search')}
            type="text"
            name="search"
            key="search"
            value={searchTerm}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                getData(0, searchTerm)
              }
            }}
            onChange={(obj) => setSearchTerm(obj.target.value)}
          />
          <CustomButton
            className="bg-primaryColor px-4"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('search')}
            onClick={() => getData(0, searchTerm)}
          />
        </div>
        <Table
          bordered
          locale={{
            emptyText: t('no_data')
          }}
          title={() => (
            <div className="flex">
              <div className="space-x-2">
                <strong>{t('total')}:</strong>
                {reportData?.report && (
                  <span>{thousand(reportData?.total)}</span>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-auto">
                {isRangeMoreThanMonth() && (
                  <Popover trigger="hover" content={t('max_of_one_month_for_export')}>
                    <div>
                      <CustomButton
                        className="bg-green-600 px-4"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="bg-green-600"
                        loading={loading}
                        disabled
                        icon={faFileExcel}
                        text={t('export')}
                        onClick={() => exportData()}
                      />
                    </div>
                  </Popover>
                )}
                {!isRangeMoreThanMonth() && (
                  <CustomButton
                    className="bg-green-600 px-4"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="bg-green-600"
                    loading={loading}
                    icon={faFileExcel}
                    text={t('export')}
                    onClick={() => exportData()}
                  />
                )}
                <strong>{t('last_updated_on')}:</strong>
                {reportData?.lastUpdatedAt && (
                  <span>{dayjs(reportData?.lastUpdatedAt).add(-4, 'hour').format('MMM DD, YYYY hh:mm A')}</span>
                )}
              </div>
            </div>
          )}
          rowKey="id"
          loading={loading}
          className="mt-2"
          pagination={{
            pageSize,
            showSizeChanger: true,
            current: currentPage + 1,
            total: reportData?.total,
            onChange: (page, size) => {
              if (page - 1 !== currentPage) {
                setCurrentPage(page - 1);
                getData(page - 1);
              } else {
                setPageSize(size);
              }
            }
          }}
          dataSource={reportData?.report}
          columns={getSourceAssetDetailsReportColumns(t)}
          // expandable={{
          //   expandRowByClick: false,
          //   rowExpandable: (record) => false,
          //   expandedRowRender: (record, index) => (
          //     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          //       <div className="col-span-1">
          //         <div>
          //           <strong>{t('mfg_part_number')}: </strong>
          //           {record.mfgPartNumber?.length > 0 && (
          //             <span>{record.mfgPartNumber}</span>
          //           )}
          //           {!record.mfgPartNumber?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('description')}: </strong>
          //           {record.shortDesc?.length > 0 && (
          //             <span>{record.shortDesc}</span>
          //           )}
          //           {!record.shortDesc?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('po_number_scan_id')}: </strong>
          //           {record.poNumberScanId?.length > 0 && (
          //             <span>{record.poNumberScanId}</span>
          //           )}
          //           {!record.poNumberScanId?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('serial_number')}: </strong>
          //           {record.serialNumber?.length > 0 && (
          //             <span>{record.serialNumber}</span>
          //           )}
          //           {!record.serialNumber?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('class')}: </strong>
          //           {record.class?.length > 0 && (
          //             <span>{record.class}</span>
          //           )}
          //           {!record.class?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('category')}: </strong>
          //           {record.category?.length > 0 && (
          //             <span>{record.category}</span>
          //           )}
          //           {!record.category?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //       </div>
          //       <div className="col-span-1">
          //         <div>
          //           <strong>{t('contact_person')}: </strong>
          //           {record.contactPerson?.length > 0 && (
          //             <span>{record.contactPerson}</span>
          //           )}
          //           {!record.contactPerson?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('order_amount')}: </strong>
          //           {record.orderAmountOc && (
          //             <span>{thousand(record.orderAmountOc)}</span>
          //           )}
          //           {!record.orderAmountOc && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('invoice_number')}: </strong>
          //           {record.invoiceNumber?.length > 0 && (
          //             <span>{record.invoiceNumber}</span>
          //           )}
          //           {!record.invoiceNumber?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('invoice_date')}: </strong>
          //           {record.invoiceDate?.length > 0 && (
          //             <span>{dayjs(record.invoiceDate).format('MMM DD, YYYY hh:mm A')}</span>
          //           )}
          //           {!record.invoiceDate?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //       </div>
          //       <div className="col-span-1">
          //         <div>
          //           <strong>{t('expected_delivery_date')}: </strong>
          //           {record.expectedDeliveryDate?.length > 0 && (
          //             <span>{dayjs(record.expectedDeliveryDate).format('MMM DD, YYYY hh:mm A')}</span>
          //           )}
          //           {!record.expectedDeliveryDate?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('within_sla')}: </strong>
          //           <span>{record.withinSla ? t('yes') : t('no')}</span>
          //         </div>
          //         <div>
          //           <strong>{t('past_due_days')}: </strong>
          //           {record.pastDueDays && (
          //             <span>{thousand(record.pastDueDays)}</span>
          //           )}
          //           {!record.pastDueDays && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('order_comment')}: </strong>
          //           {record.orderComment?.length > 0 && (
          //             <span>{record.orderComment}</span>
          //           )}
          //           {!record.orderComment?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //       </div>
          //       <div className="col-span-1">
          //         <div>
          //           <strong>{t('shipped_address')}: </strong>
          //           {record.shippedAddress?.length > 0 && (
          //             <span>{record.shippedAddress}</span>
          //           )}
          //           {!record.shippedAddress?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('shipped_country')}: </strong>
          //           {record.shippedCountry?.length > 0 && (
          //             <span>{record.shippedCountry}</span>
          //           )}
          //           {!record.shippedCountry?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('ship_method')}: </strong>
          //           {record.shipMethod?.length > 0 && (
          //             <span>{record.shipMethod}</span>
          //           )}
          //           {!record.shipMethod?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('date_shipped')}: </strong>
          //           {record.dateShipped?.length > 0 && (
          //             <span>{dayjs(record.dateShipped).format('MMM DD, YYYY hh:mm A')}</span>
          //           )}
          //           {!record.dateShipped?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('tracking_number')}: </strong>
          //           {record.trackingNumber?.length > 0 && (
          //             <span>{record.trackingNumber}</span>
          //           )}
          //           {!record.trackingNumber?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //         <div>
          //           <strong>{t('reclaim_number')}: </strong>
          //           {record.reclaimNumber?.length > 0 && (
          //             <span>{record.reclaimNumber}</span>
          //           )}
          //           {!record.reclaimNumber?.length > 0 && (
          //             <span className="text-gray-400">N/A</span>
          //           )}
          //         </div>
          //       </div>
          //     </div>
          //   )
          // }}
        />
      </div>
    </MainPage>
  );
}

export default SaleOrdersAssetDetail;
